/**
 * CmStartChecklist — mise en route de la community manager (lot 6, 2026-10-29).
 *
 * En tête du tableau de bord d'un compte du CRM M&R (origine `mr_crm`) dont l'espace a le module
 * Automatisations ; jamais pour un partenaire Mars, jamais pour un conseiller. Les étapes se cochent
 * toutes seules d'après les données de l'espace : comptes connectés, fiche produit validée, première
 * automatisation, contact de test, automatisation active, alertes Telegram (quand l'intégration existe).
 * Se replie quand tout est en place ; peut être masquée (mémorisé par utilisateur dans ce navigateur,
 * comme la visite guidée). Aucune requête nouvelle : les clés react-query sont celles des pages.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowRight, BellRing, Check, FlaskConical, Package, Play, Plug, Sparkles, X, Zap } from 'lucide-react';
import { apiRequest } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useFeatures } from '@/hooks/useFeatures';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useAccounts } from '@/hooks/usePublishing';
import { useAutomationsAccess, useProducts } from '@/lib/automations';
import { openGuidedTour } from '@/components/GuidedTour';

const STORAGE_PREFIX = 'nia-cm-start-hidden';
const storageKey = (userId: string): string => `${STORAGE_PREFIX}:${userId}`;

function isHidden(userId: string): boolean {
  try {
    return localStorage.getItem(storageKey(userId)) === '1';
  } catch {
    return false;
  }
}

function markHidden(userId: string): void {
  try {
    localStorage.setItem(storageKey(userId), '1');
  } catch {
    /* stockage indisponible : sans conséquence */
  }
}

type ItemKey = 'accounts' | 'product' | 'automation' | 'testContact' | 'activate' | 'telegram';
interface Item {
  key: ItemKey;
  done: boolean;
  to: string;
  icon: typeof Plug;
  tint: string;
}

/** Garde : ne monte la liste (et ses requêtes) que pour un compte CRM M&R dont l'espace a le module. */
export function CmStartChecklist() {
  const { user } = useAuth();
  const { automations_enabled: automationsEnabled, loaded } = useFeatures();
  const { role } = useWorkspace();
  const candidate = !!user && user.origin === 'mr_crm' && loaded && automationsEnabled === true && role !== 'conseiller';
  const access = useAutomationsAccess(candidate);
  if (!user || !candidate || access.data?.enabled !== true) return null;
  return <Checklist userId={user.id} />;
}

const settled = (q: { isFetched: boolean; isError: boolean }): boolean => q.isFetched || q.isError;

function Checklist({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { publishing_enabled: publishingEnabled, meta_features_enabled: metaEnabled } = useFeatures();
  const [hidden, setHidden] = useState(() => isHidden(userId));
  const accounts = useAccounts(publishingEnabled);
  const products = useProducts();
  const automations = useQuery({
    queryKey: ['automations'],
    queryFn: () => apiRequest<{ automations: { status: string }[] }>('/automations'),
    staleTime: 30_000,
  });
  const settings = useQuery({
    queryKey: ['automations', 'settings'],
    queryFn: () => apiRequest<{ test_contact: { platform: string; username: string } | null }>('/automations/settings'),
    staleTime: 30_000,
  });
  // L'intégration Telegram n'existe que derrière META (page Connecteurs) : hors META, l'étape n'est pas proposée.
  const telegram = useQuery({
    queryKey: ['integrations', 'telegram'],
    queryFn: () => apiRequest<{ status: string }>('/integrations/telegram'),
    staleTime: 30_000,
    retry: 0,
    enabled: metaEnabled,
  });

  if (hidden) return null;
  // Pas de clignotement : on attend les réponses ; une erreur vaut simplement « à faire ».
  const ready =
    settled(products) && settled(automations) && settled(settings) && (!publishingEnabled || settled(accounts)) && (!metaEnabled || settled(telegram));
  if (!ready) return null;

  const list = automations.data?.automations ?? [];
  const items: Item[] = [];
  if (publishingEnabled) {
    items.push({ key: 'accounts', done: (accounts.data?.accounts ?? []).some((a) => a.status === 'active'), to: '/comptes', icon: Plug, tint: '#10b981' });
  }
  items.push(
    { key: 'product', done: (products.data?.products ?? []).some((p) => !!p.validated_at), to: '/automatisations/produits', icon: Package, tint: '#ec4899' },
    { key: 'automation', done: list.length > 0, to: '/automatisations/nouvelle', icon: Zap, tint: '#8b5cf6' },
    { key: 'testContact', done: !!settings.data?.test_contact, to: '/automatisations/reglages', icon: FlaskConical, tint: '#f59e0b' },
    { key: 'activate', done: list.some((a) => a.status === 'active'), to: '/automatisations', icon: Play, tint: '#3b82f6' },
  );
  if (metaEnabled) {
    items.push({ key: 'telegram', done: telegram.data?.status === 'active', to: '/integrations', icon: BellRing, tint: '#06b6d4' });
  }
  const done = items.filter((i) => i.done).length;
  const all = done === items.length;

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-cm-start={all ? 'done' : `${done}/${items.length}`}
      aria-label={t('dashboard.cmStart.title') as string}
      className="relative mb-5 overflow-hidden rounded-[24px] border border-violet-300/50 bg-white p-5 shadow-[0_12px_30px_-18px_rgba(168,85,247,0.4)] sm:p-6"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg,#3b82f6,#a855f7,#ec4899)' }} />
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full opacity-50 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.35), transparent 70%)' }}
      />

      <header className="relative flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10.5px] font-700 uppercase tracking-[0.18em] text-pink">{t('dashboard.cmStart.kicker')}</p>
          <h2 className="mt-1 font-display text-[20px] font-800 leading-tight tracking-[-0.02em] text-ink">
            {all ? t('dashboard.cmStart.allDone') : t('dashboard.cmStart.title')}
          </h2>
          <p className="mt-1 max-w-2xl text-[13px] leading-[1.5] text-ink/60">
            {all ? t('dashboard.cmStart.allDoneBody') : t('dashboard.cmStart.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={openGuidedTour} className="btn-ghost !px-3.5 !py-2 text-[12.5px]">
            <Sparkles size={13} className="mr-1.5 inline" />
            {t('dashboard.cmStart.tour')}
          </button>
          <button
            type="button"
            onClick={() => {
              markHidden(userId);
              setHidden(true);
            }}
            aria-label={t('dashboard.cmStart.hide') as string}
            title={t('dashboard.cmStart.hide') as string}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-ink/[0.06] text-ink/55 transition hover:bg-ink/[0.12]"
          >
            <X size={14} strokeWidth={2.4} />
          </button>
        </div>
      </header>

      <div className="relative mt-4 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink/[0.06]">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(done / items.length) * 100}%`, background: 'linear-gradient(90deg,#3b82f6,#a855f7,#ec4899)' }}
          />
        </div>
        <span className="font-mono text-[11px] font-700 text-ink/60">{t('dashboard.cmStart.progress', { done, total: items.length })}</span>
      </div>

      {!all && (
        <ol className="relative mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <li key={it.key} data-cm-item={it.key} data-done={it.done ? '1' : '0'}>
                <button
                  type="button"
                  onClick={() => navigate(it.to)}
                  className={`group flex w-full items-center gap-3 rounded-2xl border px-3.5 py-3 text-left transition ${
                    it.done
                      ? 'border-emerald-200/70 bg-emerald-50/50'
                      : 'border-ink/[0.07] bg-white hover:border-violet-300/60 hover:shadow-[0_10px_24px_-16px_rgba(168,85,247,0.5)]'
                  }`}
                >
                  <span
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                    style={it.done ? { background: 'rgba(16,185,129,0.12)', color: '#059669' } : { background: `${it.tint}1a`, color: it.tint }}
                  >
                    {it.done ? <Check size={16} strokeWidth={2.6} /> : <Icon size={16} strokeWidth={2.2} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-[13.5px] font-700 leading-tight ${it.done ? 'text-ink/55 line-through decoration-emerald-400/70' : 'text-ink'}`}>
                      <span className="mr-1.5 font-mono text-[10.5px] text-ink/40">{i + 1}</span>
                      {t(`dashboard.cmStart.items.${it.key}.title`)}
                    </span>
                    <span className="mt-0.5 block text-[12px] leading-[1.4] text-ink/55">{t(`dashboard.cmStart.items.${it.key}.hint`)}</span>
                  </span>
                  <span className={`inline-flex shrink-0 items-center gap-1 text-[12px] font-700 ${it.done ? 'text-emerald-600' : 'text-pink'}`}>
                    {it.done ? t('dashboard.cmStart.done') : t('dashboard.cmStart.open')}
                    {!it.done && <ArrowRight size={13} className="transition group-hover:translate-x-0.5" />}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </motion.section>
  );
}
